import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {HELP_URLS} from '../util/constants';
import {
  Form,
  required,
  TextInput,
  useTranslate,
  useLogin,
  useNotify,
} from 'react-admin';

import Box from '@mui/material/Box';
import LostMfaModal from '../mfa/LostMfaModal';
import AccountLockedModal from '../modals/AccountLockedModal';
import LoginFailModal from '../modals/LoginFailModal';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [userId, setUserId] = useState('');
  const [credentials, setCredentials] = useState<FormValues>({});
  const [showLostMfaModal, setShowLostMfaModal] = useState(false);
  const [showAccountLockedModal, setShowAccountLockedModal] = useState(false);
  const [showLoginFailModal, setShowLoginFailModal] = useState(false);
  const [retryAfter, setRetryAfter] = useState(60);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const translate = useTranslate();
  const notify = useNotify();
  const login = useLogin();
  const location = useLocation();

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startCountdown = useCallback((seconds: number) => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    setCountdownSeconds(seconds);
    countdownTimerRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = (auth: FormValues) => {
    setLoading(true);
    setCredentials(auth);
    login(auth,location.state ? (location.state as any).nextPathname : '/')
      .then(() => {
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        // Check if MFA is required
        if (error && error.name === 'MfaRequiredError') {
          setMfaToken(error.mfa_token);
          setUserId(error.user_id);
          setShowMfa(true);
          return;
        }

      // Handle login security errors
      if (error && error.name === 'AccountLockedError') {
        setShowAccountLockedModal(true);
        return;
      }

      if (error && error.name === 'LoginFailNthError') {
        const seconds = error.retry_after || 60;
        setRetryAfter(seconds);
        setShowLoginFailModal(true);
        startCountdown(seconds);
        return;
      }

        notify(
          typeof error === 'string'
            ? error
            : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
          {
            type: 'warning',
            messageArgs: {
              _:
                typeof error === 'string'
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  const handleMfaSubmit = (values: MfaFormValues) => {
    setLoading(true);
    login(
      { ...credentials, otp: values.otp, mfa_token: mfaToken, user_id: userId },
      location.state ? (location.state as any).nextPathname : '/',
    )
      .then(() => {
        setLoading(false);
      })
      .catch((error: Error) => {
        setLoading(false);

      // Handle login security errors
      if (error && error.name === 'AccountLockedError') {
        setShowAccountLockedModal(true);
        return;
      }

      if (error && error.name === 'LoginFailNthError') {
        const seconds = error.retry_after || 60;
        setRetryAfter(seconds);
        setShowLoginFailModal(true);
        startCountdown(seconds);
        return;
      }

        notify(
          typeof error === 'string'
            ? error
            : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
          { type: 'warning' },
        );
      });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background:
          'url(/Untangl_Homepage_Image_1920x1080_V1.jpg)',
        //'url(https://docs-untangl-io.s3-eu-west-1.amazonaws.com/background/Untangl_Homepage_Image_1920x1080_V1.jpg)',
        //'url(https://source.unsplash.com/featured/1600x900)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Card sx={{ minWidth: 300, marginTop: '6em' }}>
        <Box
          sx={{
            margin: '1em',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Avatar sx={{ bgcolor: 'secondary.main' }}>
            <LockIcon />
          </Avatar>
        </Box>
        <Box
          sx={{
            marginTop: '1em',
            display: 'flex',
            justifyContent: 'center',
            color: (theme) => theme.palette.grey[500],
          }}
        ></Box>

        {/* Login Form */}
        {!showMfa && (
          <Form onSubmit={handleSubmit} noValidate>
            <Box sx={{ padding: '0 1em 1em 1em' }}>
              <Box sx={{ marginTop: '1em' }}>
                <TextInput
                  autoFocus
                  source="username"
                  label={translate('ra.auth.username')}
                  disabled={loading}
                  validate={required()}
                  fullWidth
                />
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <TextInput
                  source='password'
                  label={translate('ra.auth.password')}
                  type='password'
                  disabled={loading}
                  validate={required()}
                  fullWidth
                />
              </Box>
            </Box>
            {countdownSeconds > 0 && (
              <>
                <Box sx={{ padding: '0 1em', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                    <WarningAmberIcon color="warning" fontSize="small" />
                    <Typography variant="body2" color="warning.main" fontWeight="bold">
                      {translate('notification.retry_warning.message')}
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {formatCountdown(countdownSeconds)}
                  </Typography>
                </Box>
                <Box sx={{ padding: '0 1em 1em 1em', textAlign: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setCountdownSeconds(0);
                      if (countdownTimerRef.current) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                      }
                    }}
                  >
                    {translate('action.use_another_account')}
                  </Link>
                </Box>
              </>
            )}
            <CardActions sx={{ padding: '0 1em 1em 1em' }}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading || countdownSeconds > 0}
                fullWidth
              >
                {loading && <CircularProgress size={25} thickness={2} />}
                {countdownSeconds > 0
                  ? translate('notification.retry_warning.please_wait')
                  : translate('ra.auth.sign_in')}
              </Button>
            </CardActions>
          </Form>
        )}

        {/* MFA Form */}
        {showMfa && (
          <Form onSubmit={handleMfaSubmit} noValidate>
            <Box sx={{ padding: '0 1em 1em 1em' }}>
              <Box sx={{ marginTop: '1em' }}>
                <TextInput
                  source='username'
                  label={translate('ra.auth.username')}
                  disabled={true}
                  defaultValue={credentials.username}
                  fullWidth
                />
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <TextInput
                  source="password"
                  label={translate('ra.auth.password')}
                  type="password"
                  disabled={true}
                  defaultValue={credentials.password}
                  fullWidth
                />
              </Box>
              <Box sx={{ marginTop: '1em' }}>
                <TextInput
                  autoFocus
                   source="otp"
                  label={translate('mfa.login.verification_label')}
                  type="password"
                  disabled={loading}
                  validate={required()}
                  fullWidth
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder={translate('mfa.login.enter_code_placeholder')}
                />
              </Box>
              <Box sx={{ marginTop: '0.5em' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setShowLostMfaModal(true)}
                >
                  {translate('action.lost_mfa')}
                </Link>
              </Box>
            </Box>
            {countdownSeconds > 0 && (
              <Box sx={{ padding: '0 1em', textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                  <WarningAmberIcon color="warning" fontSize="small" />
                  <Typography variant="body2" color="warning.main" fontWeight="bold">
                    {translate('notification.retry_warning.message')}
                  </Typography>
                </Box>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  {formatCountdown(countdownSeconds)}
                </Typography>
              </Box>
            )}
            <CardActions sx={{ padding: '0 1em 1em 1em' }}>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading || countdownSeconds > 0}
                fullWidth
              >
                {loading && <CircularProgress size={25} thickness={2} />}
                {countdownSeconds > 0
                  ? translate('notification.retry_warning.please_wait')
                  : translate('mfa.login.verify_button')}
              </Button>
            </CardActions>
            <Box sx={{ padding: '0 1em 1em 1em', textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setShowMfa(false);
                  setMfaToken('');
                  setUserId('');
                  setCountdownSeconds(0);
                  if (countdownTimerRef.current) {
                    clearInterval(countdownTimerRef.current);
                    countdownTimerRef.current = null;
                  }
                }}
              >
                {translate('action.use_another_account')}
              </Link>
            </Box>
          </Form>
        )}
      </Card>

      {/* Lost MFA Modal */}
      <LostMfaModal
        open={showLostMfaModal}
        onClose={() => setShowLostMfaModal(false)}
      />

      {/* Account Locked Modal */}
      <AccountLockedModal
        open={showAccountLockedModal}
        onClose={() => setShowAccountLockedModal(false)}
      />

      {/* Login Fail Modal */}
      <LoginFailModal
        open={showLoginFailModal}
        onClose={() => setShowLoginFailModal(false)}
        retryAfter={retryAfter}
      />
    </Box>
  );
};

Login.propTypes = {
  authProvider: PropTypes.func,
  previousRoute: PropTypes.string,
};

export default Login;

interface FormValues {
  username?: string;
  password?: string;
}

interface MfaFormValues {
  otp?: string;
}
